import * as React from "react"
import { useCurrentUser } from "context/db"
import { luzPlans, PlanFeatureTypes, LuzPlanFeatures } from "util/constants"
import { UserType } from "types"

export function hasPlanFeatures({
  user,
  allowedCompanyId,
  allowedFeatures = [],
}: {
  user: UserType | undefined
  allowedCompanyId?: string
  allowedFeatures?: PlanFeatureTypes[]
}) {
  const currentUsersPlan = user?.group?.plan_id
  const currentUsersPlanFeatures: LuzPlanFeatures | undefined =
    user?.group?.plan_features
  const currentUsersPremiumCompanyIds =
    user?.group?.purchased_companies?.map(company => company.id) ?? []

  let hasAccess = false
  // allow all internal users higher privileges to access whatever
  if (currentUsersPlan === "internal") {
    hasAccess = true
  }

  // allow users that pay for a company to access if allowedCompanyId is passed in
  if (allowedCompanyId) {
    if (currentUsersPremiumCompanyIds.includes(allowedCompanyId)) {
      hasAccess = true
    }
  }

  allowedFeatures.forEach((feature: PlanFeatureTypes) => {
    if (currentUsersPlanFeatures?.[feature]) {
      hasAccess = true
    } else if (
      currentUsersPlan &&
      currentUsersPlan in luzPlans &&
      luzPlans[currentUsersPlan][feature] === true
    ) {
      // TODO: delete this when we move to flat plan feature structure
      hasAccess = true
    }
  })

  return hasAccess
}

// used to lock down components based on a user's plan:
// |             |  retail (starter)  |   retail (pro)    |    retail (plus)    |   retail (enterprise)  |  nonretail  |
// | api access  |          ❌                 ❌                  ✅                        ✅                ✅
// | historical  |
// | ...etc.     |

export default function PlanGate({
  children,
  allowedFeatures = [], // "apiAccess", "historicalData", etc.
  allowedCompanyId,
  publicChildren,
  pendingCompanyChildren,
}: {
  children: React.ReactNode
  /** the features that a user's group needs to have in order to view the gated content */
  allowedFeatures?: PlanFeatureTypes[]
  /** the company ID that should allow access to the gated content, if the user's group has added the company */
  allowedCompanyId?: string
  publicChildren?: React.ReactNode
  pendingCompanyChildren?: React.ReactNode
}) {
  const { data } = useCurrentUser()

  return (
    <PurePlanGate
      currentUser={data}
      allowedFeatures={allowedFeatures}
      allowedCompanyId={allowedCompanyId}
      publicChildren={publicChildren}
      pendingCompanyChildren={pendingCompanyChildren}
    >
      {children}
    </PurePlanGate>
  )
}

// easily testable pure function
export function PurePlanGate({
  currentUser,
  allowedFeatures = [],
  allowedCompanyId,
  publicChildren,
  pendingCompanyChildren,
  children,
}: {
  currentUser: UserType
  children: React.ReactNode
  allowedFeatures: PlanFeatureTypes[]
  allowedCompanyId?: string
  publicChildren?: React.ReactNode
  pendingCompanyChildren?: React.ReactNode
}): any {
  const hasAccess = hasPlanFeatures({
    user: currentUser,
    allowedCompanyId,
    allowedFeatures,
  })

  let isPendingCompany = false
  const currentUsersPendingCompanyIds =
    currentUser?.group?.pending_companies?.map(company => company.id) ?? []

  // check if the user has added this company but it is pending to be verified by ops
  if (allowedCompanyId) {
    if (currentUsersPendingCompanyIds.includes(allowedCompanyId)) {
      isPendingCompany = true
    }
  }

  return hasAccess
    ? children
    : isPendingCompany
    ? pendingCompanyChildren || publicChildren
    : publicChildren || null
}
